import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  onOpenCookieSettings,
  readConsent,
  saveConsent,
  type OptionalCategory,
} from "@/lib/cookieConsent";

const OPTIONAL: OptionalCategory[] = ["preferences", "functional"];

/**
 * The first-visit banner and the settings dialog. The banner shows until the
 * visitor chooses (and again once the choice lapses); the dialog can be
 * reopened any time from the footer or the Cookie Policy.
 */
export default function CookieConsent() {
  const { t } = useTranslation();
  const [bannerOpen, setBannerOpen] = useState(() => readConsent() === null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [choice, setChoice] = useState<Record<OptionalCategory, boolean>>(() => {
    const c = readConsent();
    return { preferences: c?.preferences ?? false, functional: c?.functional ?? false };
  });

  useEffect(
    () =>
      onOpenCookieSettings(() => {
        const c = readConsent();
        setChoice({ preferences: c?.preferences ?? false, functional: c?.functional ?? false });
        setSettingsOpen(true);
      }),
    []
  );

  useEffect(() => {
    if (!settingsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [settingsOpen]);

  const decide = (next: Record<OptionalCategory, boolean>) => {
    saveConsent(next);
    setChoice(next);
    setBannerOpen(false);
    setSettingsOpen(false);
  };
  const acceptAll = () => decide({ preferences: true, functional: true });
  const rejectOptional = () => decide({ preferences: false, functional: false });

  const secondaryBtn =
    "whitespace-nowrap rounded-full border border-background-300/70 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer";
  const primaryBtn =
    "whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 cursor-pointer";

  return (
    <>
      {bannerOpen && !settingsOpen && (
        <div
          role="region"
          aria-label={t("common.cookies.banner.title")}
          className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4"
        >
          <div className="mx-auto max-w-4xl rounded-2xl border border-background-200/70 bg-background-50 p-5 shadow-2xl md:flex md:items-center md:gap-6">
            <div className="flex-1">
              <h2 className="text-base font-bold text-foreground-950">{t("common.cookies.banner.title")}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                {t("common.cookies.banner.text")}{" "}
                <Link to="/cookie-policy" className="font-semibold text-primary-600 hover:underline">
                  {t("common.legal.cookiePolicy")}
                </Link>
              </p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 md:mt-0 md:flex-nowrap">
              <button type="button" onClick={() => setSettingsOpen(true)} className={secondaryBtn}>
                {t("common.cookies.customize")}
              </button>
              <button type="button" onClick={rejectOptional} className={secondaryBtn}>
                {t("common.cookies.rejectOptional")}
              </button>
              <button type="button" onClick={acceptAll} className={primaryBtn}>
                {t("common.cookies.acceptAll")}
              </button>
            </div>
          </div>
        </div>
      )}

      {settingsOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-foreground-950/50 p-3 sm:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSettingsOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-settings-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-background-50 p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <h2 id="cookie-settings-title" className="text-lg font-bold text-foreground-950">
                {t("common.cookies.settingsTitle")}
              </h2>
              <button
                type="button"
                onClick={() => setSettingsOpen(false)}
                aria-label={t("common.actions.close")}
                className="text-xl text-foreground-500 hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              {t("common.cookies.settingsIntro")}{" "}
              <Link
                to="/cookie-policy"
                onClick={() => setSettingsOpen(false)}
                className="font-semibold text-primary-600 hover:underline"
              >
                {t("common.legal.cookiePolicy")}
              </Link>
            </p>

            <ul className="mt-5 divide-y divide-background-200/70 rounded-xl border border-background-200/70">
              <li className="flex items-start justify-between gap-4 p-4">
                <div>
                  <div className="text-sm font-semibold text-foreground-950">
                    {t("common.cookies.categories.necessary.name")}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                    {t("common.cookies.categories.necessary.description")}
                  </p>
                </div>
                <span className="whitespace-nowrap text-xs font-semibold text-primary-600">
                  {t("common.cookies.alwaysOn")}
                </span>
              </li>
              {OPTIONAL.map((category) => (
                <li key={category} className="flex items-start justify-between gap-4 p-4">
                  <div>
                    <label
                      htmlFor={`cookie-${category}`}
                      className="text-sm font-semibold text-foreground-950 cursor-pointer"
                    >
                      {t(`common.cookies.categories.${category}.name`)}
                    </label>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                      {t(`common.cookies.categories.${category}.description`)}
                    </p>
                  </div>
                  <input
                    id={`cookie-${category}`}
                    type="checkbox"
                    role="switch"
                    checked={choice[category]}
                    onChange={(e) => setChoice({ ...choice, [category]: e.target.checked })}
                    className="mt-1 h-5 w-5 shrink-0 accent-primary-600 cursor-pointer"
                  />
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={rejectOptional} className={secondaryBtn}>
                {t("common.cookies.rejectOptional")}
              </button>
              <button type="button" onClick={() => decide(choice)} className={secondaryBtn}>
                {t("common.cookies.save")}
              </button>
              <button type="button" onClick={acceptAll} className={primaryBtn}>
                {t("common.cookies.acceptAll")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
